if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      variantPriceUnsubscriber = undefined;
      quantityPriceUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      storedUnitPriceCents = null;
      currentVariant = null;
      expectedUnitPriceFormatted = '';
      lockingPrice = false;
      unitPriceObserver = undefined;
      syncingQuantityForVariant = false;
      quantitySyncLockCount = 0;
      isHandlingVariantUpdate = false;
      lastFetchedSectionHtml = null;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');

        this.isFeaturedProduct = this.closest('.section-featured-product');

        this.cleanHiddenProductInfoContainers = this.cleanHiddenProductInfoContainers.bind(this);
        document.addEventListener('DOMContentLoaded', this.cleanHiddenProductInfoContainers);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        const initialVariantJson = this.getActiveVariantSelects()?.querySelector('[data-selected-variant]');
        if (initialVariantJson?.textContent) {
          try {
            this.currentVariant = JSON.parse(initialVariantJson.textContent);
          } catch {
            this.currentVariant = null;
          }
        }

        this.initUnitPriceHandlers();
        this.updateDrapeCustomPropertyVisibility(this.currentVariant);

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // If the DOM is already loaded by the time the component is added to the DOM
        if (document.readyState === 'interactive' || document.readyState === 'complete') {
          this.cleanHiddenProductInfoContainers();
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        this.quantityInputs = this.querySelectorAll('.quantity__input');
        if (!this.quantityInputs.length) return;

        this.quantityInput = this.quantityInputs[0];
        this.quantityForm =
          this.querySelector('.product-form__quantity') || this.querySelector('.product-form__buttons');

        this.quantityInputs.forEach((input) => this.applyQuantityBoundaries(input));

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      formatMoney(cents, formatString) {
        const format = formatString || '${{amount}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;

        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          const normalized = (number / 100).toFixed(precision);
          const parts = normalized.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const centsPart = parts[1] ? decimal + parts[1] : '';
          return dollars + centsPart;
        };

        const match = format.match(placeholder);
        if (!match) return formatWithDelimiters(cents, 2);

        let value;
        switch (match[1]) {
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount':
          default:
            value = formatWithDelimiters(cents, 2);
            break;
        }

        return format.replace(placeholder, value);
      }

      getActiveVariantSelects() {
        const variantSelects = Array.from(this.querySelectorAll('variant-selects')).filter(
          (element) => element.style.display !== 'none'
        );
        return variantSelects[variantSelects.length - 1] || this.querySelector('variant-selects');
      }

      getVariantPriceCents(variant) {
        if (!variant) return this.getSelectedVariantPriceCents();

        if (typeof variant.price === 'number' && !isNaN(variant.price)) {
          return variant.price;
        }

        const parsed = parseInt(variant.price, 10);
        return isNaN(parsed) ? this.getSelectedVariantPriceCents() : parsed;
      }

      getSelectedVariantPriceCents() {
        const variantJson = this.getActiveVariantSelects()?.querySelector('[data-selected-variant]');
        if (!variantJson?.textContent) return null;
        try {
          const variant = JSON.parse(variantJson.textContent);
          return this.getVariantPriceCents(variant);
        } catch {
          return null;
        }
      }

      getAddonPriceCents() {
        return Array.from(this.querySelectorAll('.js-custom-property-price-addon:checked')).reduce(
          (sum, input) => sum + (parseInt(input.dataset.addonPriceCents, 10) || 0),
          0
        );
      }

      getPriceContainer() {
        return this.querySelector(`#price-${this.dataset.section}`) || this.querySelector('[id^="price-"]');
      }

      getMainPriceElements(priceContainer) {
        if (!priceContainer) return [];

        const priceRoot = priceContainer.querySelector('.price') || priceContainer;
        const elements = [];
        const onSale = priceRoot.classList.contains('price--on-sale');

        if (onSale) {
          priceRoot.querySelectorAll('.price__sale .price-item--last').forEach((el) => elements.push(el));
        } else {
          priceRoot.querySelectorAll('.price__regular .price-item--regular').forEach((el) => {
            if (!el.closest('s')) elements.push(el);
          });
        }

        return elements;
      }

      setMainPriceText(priceContainer, formatted) {
        if (!priceContainer || !formatted) return;

        this.getMainPriceElements(priceContainer).forEach((el) => {
          el.textContent = formatted;
        });
      }

      syncUnitPriceFromFetchedHtml(html) {
        const source = html.getElementById(`price-${this.sectionId}`);
        const destination = this.getPriceContainer();
        if (!source || !destination) return;

        const sourcePrice = source.querySelector('.price');
        const destinationPrice = destination.querySelector('.price') || destination;
        if (!sourcePrice || !destinationPrice) return;

        const onSale = sourcePrice.classList.contains('price--on-sale');
        let sourceText = '';
        let destinationSelector;

        if (onSale) {
          sourceText = sourcePrice.querySelector('.price__sale .price-item--last')?.textContent?.trim() || '';
          destinationSelector = '.price__sale .price-item--last';
        } else {
          const sourceRegular = Array.from(sourcePrice.querySelectorAll('.price__regular .price-item--regular')).find(
            (el) => !el.closest('s')
          );
          sourceText = sourceRegular?.textContent?.trim() || '';
          destinationSelector = '.price__regular .price-item--regular';
        }

        if (!sourceText) return;

        this.lockingPrice = true;
        try {
          if (onSale) {
            const el = destinationPrice.querySelector(destinationSelector);
            if (el) el.textContent = sourceText;
          } else {
            Array.from(destinationPrice.querySelectorAll(destinationSelector)).forEach((el) => {
              if (!el.closest('s')) el.textContent = sourceText;
            });
          }
          this.expectedUnitPriceFormatted = sourceText;
        } finally {
          this.lockingPrice = false;
        }
      }

      initUnitPriceHandlers() {
        const syncPriceFromQuantityEvent = (event) => {
          if (this.syncingQuantityForVariant) return;
          if (event.target?.matches?.('.quantity__input')) {
            this.scheduleUnitPriceSync();
          }
        };

        this.addEventListener('change', syncPriceFromQuantityEvent);
        this.addEventListener('input', syncPriceFromQuantityEvent);

        this.addEventListener(
          'click',
          (event) => {
            if (this.syncingQuantityForVariant) return;
            if (event.target.closest('.quantity__button')) {
              this.scheduleUnitPriceSync();
            }
          },
          true
        );

        this.variantPriceUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          if (this.isHandlingVariantUpdate) return;
          if (event.data?.variant) this.currentVariant = event.data.variant;
          this.updateDrapeCustomPropertyVisibility(event.data?.variant);
          this.scheduleUnitPriceSync(event.data?.variant, { fetchedHtml: this.lastFetchedSectionHtml });
        });

        this.quantityPriceUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          if (this.syncingQuantityForVariant) return;
          this.scheduleUnitPriceSync();
        });

        this.startUnitPriceObserver();
        this.scheduleUnitPriceSync();
      }

      startUnitPriceObserver() {
        const container = this.getPriceContainer();
        if (!container || this.unitPriceObserver) return;

        this.unitPriceObserver = new MutationObserver(() => {
          if (this.lockingPrice || this.syncingQuantityForVariant || !this.expectedUnitPriceFormatted) return;

          const mismatched = this.getMainPriceElements(container).some(
            (el) => el.textContent.trim() !== this.expectedUnitPriceFormatted
          );

          if (mismatched) {
            if (this.lastFetchedSectionHtml) {
              this.syncUnitPriceFromFetchedHtml(this.lastFetchedSectionHtml);
            }
            this.scheduleUnitPriceSync();
          }
        });

        this.unitPriceObserver.observe(container, {
          characterData: true,
          childList: true,
          subtree: true,
        });
      }

      startVariantQuantitySync() {
        this.quantitySyncLockCount += 1;
        this.syncingQuantityForVariant = true;
      }

      endVariantQuantitySync() {
        this.quantitySyncLockCount = Math.max(0, this.quantitySyncLockCount - 1);
        if (this.quantitySyncLockCount === 0) {
          this.syncingQuantityForVariant = false;
        }
      }

      scheduleUnitPriceSync(variant, { fetchedHtml = null, onComplete = null } = {}) {
        const apply = () => {
          if (fetchedHtml) {
            this.syncUnitPriceFromFetchedHtml(fetchedHtml);
          }
          this.updateDisplayedUnitPrice(variant);
        };

        apply();
        requestAnimationFrame(apply);

        const delays = [0, 50, 150, 300];
        delays.forEach((delay, index) => {
          setTimeout(() => {
            apply();
            if (onComplete && index === delays.length - 1) {
              onComplete();
            }
          }, delay);
        });
      }

      updateDisplayedUnitPrice(variant) {
        const effectiveVariant = variant || this.currentVariant;
        const baseCents = this.getVariantPriceCents(effectiveVariant);
        if (baseCents === null) return;

        this.storedUnitPriceCents = baseCents;

        const totalCents = baseCents + this.getAddonPriceCents();
        const format =
          this.dataset.moneyWithCurrencyFormat ||
          this.dataset.moneyFormat ||
          window.theme?.settings?.moneyWithCurrencyFormat ||
          window.theme?.settings?.moneyFormat;
        const formatted = this.formatMoney(totalCents, format);

        this.lockingPrice = true;
        try {
          this.expectedUnitPriceFormatted = formatted;
          this.setMainPriceText(this.getPriceContainer(), formatted);

          const stickyPrice = this.querySelector('.sticky-cta__price');
          if (stickyPrice) {
            stickyPrice.textContent = formatted;
          }
        } finally {
          this.lockingPrice = false;
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.variantPriceUnsubscriber?.();
        this.quantityPriceUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.unitPriceObserver?.disconnect();

        document.removeEventListener('DOMContentLoaded', this.cleanHiddenProductInfoContainers);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        // Remove any opened PDP modals
        document.querySelectorAll('.popup-modal--pdp').forEach((modal) => modal.remove());

        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        // Soft disable dropdowns
        this.querySelectorAll('variant-selects .nice__select').forEach(select => {
          select.classList.add('disabled');
        });

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          }).finally(() => {
            // Soft enable dropdowns
            this.querySelectorAll('variant-selects .nice__select').forEach(select => {
              select.classList.remove('disabled');
            });
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          this.currentVariant = variant;
          this.lastFetchedSectionHtml = html;
          this.isHandlingVariantUpdate = true;
          this.startVariantQuantitySync();

          const finishVariantUpdate = () => {
            this.isHandlingVariantUpdate = false;
            this.endVariantQuantitySync();
          };

          try {
            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            this.updateURL(productUrl, variant?.id);
            this.updateVariantDataVisibility(variant?.id);
            this.updateDrapeCustomPropertyVisibility(variant);

            if (!variant) {
              this.setUnavailable();
              finishVariantUpdate();
              return;
            }

            // Reset quantity to the new variant minimum before any price DOM updates.
            this.updateQuantityRules(this.sectionId, html);
            this.applyVariantQuantityRules(variant);

            this.updateVariantInputs(variant?.id);
            this.updateMedia(html, variant?.featured_media?.id);

            const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
              const source = html.getElementById(`${id}-${this.sectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML.replace(/<template(?![^>]*\bdata-prevent-interpolation\b)[^>]*>[\s\S]*?<\/template>/g, '');;
                destination.classList.toggle('hidden', shouldHide(source));
                destination.classList.toggle('visibility-hidden', shouldHide(source));
              }
            };

            updateSourceFromDestination('price');
            this.syncUnitPriceFromFetchedHtml(html);
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

            updateSourceFromDestination('back-in-stock-enquiry');
            updateSourceFromDestination('product-enquiry');
            updateSourceFromDestination('Barcode');
            if (!this.isFeaturedProduct) updateSourceFromDestination('sticky-cta');

            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );

            window.renderSelects();

            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });

            this.scheduleUnitPriceSync(variant, {
              fetchedHtml: html,
              onComplete: finishVariantUpdate,
            });
          } catch (error) {
            finishVariantUpdate();
            throw error;
          }
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const shareButton = this.querySelector('share-button');
        if (shareButton) {
          shareButton.init();
          shareButton.updateUrl(
            `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
          );
        }

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));

        this.querySelector('.product')?.setAttribute('data-variant-status', 'unavailable');
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      getVariantQuantityMin(variant) {
        if (!variant) return 1;

        let qtyMin = variant.quantity_rule?.min ?? 1;
        if (qtyMin < 1) qtyMin = 1;

        const optionParts = [];
        if (variant.title) optionParts.push(variant.title);
        if (Array.isArray(variant.options)) {
          variant.options.forEach((opt) => {
            optionParts.push(typeof opt === 'object' ? opt.name || opt : opt);
          });
        }
        if (variant.option1) optionParts.push(variant.option1);
        if (variant.option2) optionParts.push(variant.option2);
        if (variant.option3) optionParts.push(variant.option3);
        const variantOptionText = optionParts.join(' ');

        const boltValues = ['Bolt', 'Bolts'];
        const isBolt =
          (Array.isArray(variant.options) &&
            variant.options.some((opt) => {
              const label = typeof opt === 'object' ? opt.name || opt : opt;
              return boltValues.includes(label);
            })) ||
          boltValues.includes(variant.option1) ||
          boltValues.includes(variant.option2) ||
          boltValues.includes(variant.option3);

        if (isBolt) {
          return 1;
        }

        const yardValues = ['Yard', 'Yards'];
        const isYardUnit =
          (Array.isArray(variant.options) &&
            variant.options.some((opt) => {
              const label = typeof opt === 'object' ? opt.name || opt : opt;
              return yardValues.includes(label);
            })) ||
          yardValues.includes(variant.option1) ||
          yardValues.includes(variant.option2) ||
          yardValues.includes(variant.option3) ||
          variant.title?.includes('Yards') ||
          variant.title?.includes(' / Yard');

        if (isYardUnit && qtyMin < 2) qtyMin = 2;
        if (
          (variantOptionText.includes('Overlay') || variantOptionText.includes('Tall')) &&
          qtyMin < 6
        ) {
          qtyMin = 6;
        }
        if (variantOptionText.includes('Chair Cover') && qtyMin < 25) qtyMin = 25;
        if (variantOptionText.includes('Runner') && qtyMin < 12) qtyMin = 12;
        if (variantOptionText.includes('Napkin') && qtyMin < 60) qtyMin = 60;
        if (variantOptionText.includes('Sash') && qtyMin < 49) qtyMin = 49;

        return qtyMin;
      }

      getVariantQuantityMax(variant) {
        if (!variant) return null;

        let qtyMax = variant.quantity_rule?.max ?? null;
        const isSample =
          (Array.isArray(variant.options) &&
            variant.options.some((opt) => {
              const label = typeof opt === 'object' ? opt.name || opt : opt;
              return label === 'Sample';
            })) ||
          variant.option1 === 'Sample' ||
          variant.option2 === 'Sample' ||
          variant.option3 === 'Sample' ||
          variant.title?.includes(' / Sample') ||
          variant.title?.includes('Sample /');

        if (isSample && (qtyMax === null || qtyMax > 1)) {
          return 1;
        }

        return qtyMax;
      }

      applyVariantQuantityRules(variant) {
        if (!variant) return;

        const currentQuantityInputs = this.querySelectorAll('.quantity__input');
        if (!currentQuantityInputs.length) return;

        const min = this.getVariantQuantityMin(variant);
        const max = this.getVariantQuantityMax(variant);
        const step = variant.quantity_rule?.increment ?? 1;

        currentQuantityInputs.forEach((input) => {
          input.dataset.min = String(min);
          input.min = min;
          input.step = step;
          input.value = String(min);

          if (max != null) {
            input.dataset.max = String(max);
            input.max = max;
          } else {
            input.removeAttribute('data-max');
            input.removeAttribute('max');
          }

          this.applyQuantityBoundaries(input, { resetToMin: true, silent: true });
        });
      }

      getUpdatedQuantityInput(html, sectionId) {
        return (
          html.querySelector(`#Quantity-Form-${sectionId}-inline .quantity__input`) ||
          html.getElementById(`Quantity-Form-${sectionId}`)?.querySelector('.quantity__input') ||
          html.querySelector(`#Quantity-${sectionId}-inline`) ||
          html.querySelector(`#Quantity-${sectionId}`) ||
          html.querySelector('.product-form--buy-buttons .quantity__input')
        );
      }

      syncQuantityInputFromSource(current, updated) {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'min', 'max', 'step', 'value'];
        for (const attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
            if (attribute === 'value') {
              current.value = valueUpdated;
            }
          } else {
            current.removeAttribute(attribute);
          }
        }
      }

      applyQuantityBoundaries(input, { resetToMin = false, silent = false } = {}) {
        const data = {
          cartQuantity: input.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity, 10) : 0,
          min: input.dataset.min ? parseInt(input.dataset.min, 10) : 1,
          max: input.dataset.max ? parseInt(input.dataset.max, 10) : null,
          step: input.step ? parseInt(input.step, 10) : 1,
        };

        let min = data.min;
        let max = data.max === null ? null : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        input.min = min;

        if (max) {
          input.max = max;
        } else {
          input.removeAttribute('max');
        }

        const previousValue = input.value;
        let value;
        if (resetToMin) {
          value = min;
        } else {
          value = parseInt(input.value, 10);
          if (isNaN(value)) value = min;
          if (max !== null) value = Math.min(value, max);
          value = Math.max(value, min);
        }
        input.value = value;
        if (!silent && previousValue !== String(value)) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      setQuantityBoundries() {
        const inputs = this.querySelectorAll('.quantity__input');
        inputs.forEach((input) => this.applyQuantityBoundaries(input));
        if (!this.syncingQuantityForVariant) {
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden', 'spinning-complete');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const variant = this.getSelectedVariant(html);
            this.currentVariant = variant;
            this.lastFetchedSectionHtml = html;
            this.startVariantQuantitySync();
            this.updateQuantityRules(this.dataset.section, html);
            this.applyVariantQuantityRules(variant);
            this.syncUnitPriceFromFetchedHtml(html);
            this.scheduleUnitPriceSync(variant, {
              fetchedHtml: html,
              onComplete: () => this.endVariantQuantitySync(),
            });
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden', 'spinning-complete'));
      }

      updateQuantityRules(sectionId, html) {
        const updatedQuantityInput = this.getUpdatedQuantityInput(html, sectionId);
        const currentQuantityInputs = this.querySelectorAll('.quantity__input');
        if (!updatedQuantityInput || !currentQuantityInputs.length) return;

        currentQuantityInputs.forEach((current) => {
          this.syncQuantityInputFromSource(current, updatedQuantityInput);
          this.applyQuantityBoundaries(current, { resetToMin: true, silent: true });
        });

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (this.quantityForm && quantityFormUpdated) {
          const selectors = ['.quantity__rules', '.quantity__label'];
          for (const selector of selectors) {
            const current = this.quantityForm.querySelector(selector);
            const updated = quantityFormUpdated.querySelector(selector);
            if (!current || !updated) continue;
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }

        if (!this.syncingQuantityForVariant) {
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
      }

      cleanHiddenProductInfoContainers() {
        if (!Shopify.designMode) {
          document.querySelectorAll('.product__info-container[hidden]').forEach((element) => {
            element.remove();
          });
        }
      }

      updateVariantDataVisibility(variantId) {
        document.querySelectorAll(`[data-variant-data-product-id='${this.dataset.productId}']`).forEach(dataPiece => {
          dataPiece.classList.toggle('hidden', dataPiece.dataset.variantDataVariantId != variantId);
        });
      }

      variantHasDrapeOption(variant) {
        if (!variant) return false;

        const optionParts = [];
        if (variant.title) optionParts.push(variant.title);
        if (Array.isArray(variant.options)) {
          variant.options.forEach((opt) => {
            optionParts.push(typeof opt === 'object' ? opt.name || opt : opt);
          });
        }
        if (variant.option1) optionParts.push(variant.option1);
        if (variant.option2) optionParts.push(variant.option2);
        if (variant.option3) optionParts.push(variant.option3);

        return optionParts.join(' ').includes('Drape');
      }

      updateDrapeCustomPropertyVisibility(variant) {
        const showDrapeFields = this.variantHasDrapeOption(variant);

        this.querySelectorAll('.js-drape-only-custom-property').forEach((property) => {
          property.classList.toggle('hidden', !showDrapeFields);

          property.querySelectorAll('select, input:not([type="hidden"]), textarea').forEach((field) => {
            const shouldRequire = field.dataset.drapeRequired === 'true';

            if (!showDrapeFields) {
              if (field.required) {
                field.dataset.wasRequired = 'true';
                field.required = false;
                field.removeAttribute('aria-required');
              }
              if (field.tagName === 'SELECT') {
                field.value = '';
              }
            } else if (shouldRequire || field.dataset.wasRequired === 'true') {
              field.required = true;
              field.setAttribute('aria-required', 'true');
              delete field.dataset.wasRequired;
            }
          });
        });
      }


      get productForm() {
        const productForm = this.querySelector('.product-form--buy-buttons');
        if (productForm) {
          productForm.init();
        }
        return productForm;
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
